import { A11yModule } from "@angular/cdk/a11y";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ErrorHandler, NgModule, inject, provideAppInitializer } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { DateAdapter, MAT_DATE_LOCALE, NativeDateAdapter } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatSortModule } from "@angular/material/sort";
import { MatTableModule } from "@angular/material/table";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router } from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { NgIdleKeepaliveModule } from "@ng-idle/keepalive";
import * as Sentry from "@sentry/angular";
import { DragulaModule } from "ng2-dragula";
import { ColorCompactModule } from "ngx-color/compact";
// <color-compact></color-compact>
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { QuillModule } from "ngx-quill";
import { environment } from "src/environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { JwtInterceptor } from "./interceptor/jwt.interceptor";
import { ErrorHandlerService } from "./services/error/error-handler.service";
import { ModalService } from "./services/modal.service";
import { ThemeService } from "./services/theme/theme.service";
import { VersionService } from "./services/version/version.service";
import { MainTemplateComponent } from "./template/main-template/main-template.component";
import { OptOutComponent } from "./view/admin-client/opt-out/opt-out.component";
import { StopAutoEmailsComponent } from "./view/admin-client/stop-auto-emails/stop-auto-emails.component";
import { AuditLoggerUserImpersonationHistoryComponent } from "./view/audit-logger-user-impersonation/audit-log-history/audit-logger-user-impersonation-history.component";
import { AuditLoggerUserImpersonationComponent } from "./view/audit-logger-user-impersonation/audit-logger-user-impersonation/audit-logger-user-impersonation.component";
import { AuditLogHistoryComponent } from "./view/audit-logger/audit-log-history/audit-log-history.component";
import { AuditLoggerComponent } from "./view/audit-logger/audit-logger/audit-logger.component";
import { ForgotPasswordComponent } from "./view/auth/forgot-password/forgot-password.component";
import { LoggedOutComponent } from "./view/auth/logged-out/logged-out.component";
import { LoginComponent } from "./view/auth/login/login.component";
import { MFAVerificationComponent } from "./view/auth/mfa-verification/mfa-verification.component";
import { ResetPasswordComponent } from "./view/auth/reset-password/reset-password.component";
import { AdminDashboardComponent } from "./view/dashboard-admin/admin-dashboard/admin-dashboard.component";
import { CorporateReportingComponent } from "./view/dashboard-admin/corporate-reporting/corporate-reporting.component";
import { ClientDashboardComponent } from "./view/dashboard-client/client-dashboard/client-dashboard.component";
import { DRODashboardComponent } from "./view/dashboard-dro/dro-dashboard/dro-dashboard.component";
import { GenerateBenchmarkComponent } from "./view/dashboard-dro/generate-benchmark/generate-benchmark.component";
import { AddEditListVariantsComponent } from "./view/dashboard-survey-products/add-edit-list-variants/add-edit-list-variants.component";
import { AddEditQgroupComponent } from "./view/dashboard-survey-products/add-edit-qgroup/add-edit-qgroup.component";
import { AddEditQuestionComponent } from "./view/dashboard-survey-products/add-edit-question/add-edit-question.component";
import { AddEditRangeVariantsComponent } from "./view/dashboard-survey-products/add-edit-range-variants/add-edit-range-variants.component";
import { AddEditRangeWeightedComponent } from "./view/dashboard-survey-products/add-edit-range-weighted/add-edit-range-weighted.component";
import { AddEditResponseOptionGroupComponent } from "./view/dashboard-survey-products/add-edit-response-option-group/add-edit-response-option-group.component";
import { AddListOptionsComponent } from "./view/dashboard-survey-products/add-list-options/add-list-options.component";
import { AddRangeOptionsComponent } from "./view/dashboard-survey-products/add-range-options/add-range-options.component";
import { CompanyBrandingComponent } from "./view/dashboard-survey-products/branding/company/company-branding.component";
import { BrandingComponent } from "./view/dashboard-survey-products/branding/manager/branding.component";
import { CreateQuestionnaireComponent } from "./view/dashboard-survey-products/create-questionnaire/create-questionnaire.component";
import { ListOptionsComponent } from "./view/dashboard-survey-products/list-options/list-options.component";
import { ListQuestionsComponent } from "./view/dashboard-survey-products/list-questions/list-questions.component";
import { ListRangeOptionsComponent } from "./view/dashboard-survey-products/list-range-options/list-range-options.component";
import { ListRangeVariantsComponent } from "./view/dashboard-survey-products/list-range-variants/list-range-variants.component";
import { ListRangeWeightedComponent } from "./view/dashboard-survey-products/list-range-weighted/list-range-weighted.component";
import { ManageListVariantsComponent } from "./view/dashboard-survey-products/manage-list-variants/manage-list-variants.component";
import { QgroupListComponent } from "./view/dashboard-survey-products/qgroup-list/qgroup-list.component";
import { QuestionnaireLibraryComponent } from "./view/dashboard-survey-products/questionnaire-library/questionnaire-library.component";
import { ResponseOptionGroupDuplicateModalComponent } from "./view/dashboard-survey-products/response-option-group-duplicate-modal/response-option-group-duplicate-modal.component";
import { ResponseOptionsLibraryComponent } from "./view/dashboard-survey-products/response-options-library/response-options-library.component";
import { SurveyProductListComponent } from "./view/dashboard-survey-products/survey-product-list/survey-product-list.component";
import { MergeFieldDictionaryComponent } from "./view/dashboard-survey-products/survey-product-wizard/steps/step-attach-questionnaires/merge-field-dictionary/merge-field-dictionary.component";
import { AttachQuestionnairesComponent } from "./view/dashboard-survey-products/survey-product-wizard/steps/step-attach-questionnaires/step-attach-questionnaires.component";
import { StepBrandingComponent } from "./view/dashboard-survey-products/survey-product-wizard/steps/step-branding/step-branding.component";
import { StepMetadataComponent } from "./view/dashboard-survey-products/survey-product-wizard/steps/step-metadata/step-metadata.component";
import { StepPublishingWorkflowComponent } from "./view/dashboard-survey-products/survey-product-wizard/steps/step-publishing-workflow/step-publishing-workflow.component";
import { SurveyProductWizardComponent } from "./view/dashboard-survey-products/survey-product-wizard/survey-product-wizard.component";
import { SurveyProductsDashboardComponent } from "./view/dashboard-survey-products/survey-products-dashboard/survey-products-dashboard.component";
import { TemplateGroupingDetailComponent } from "./view/dashboard-survey-products/template-groupings/template-grouping-detail/template-grouping-detail.component";
import { TemplateGroupingsListComponent } from "./view/dashboard-survey-products/template-groupings/template-groupings-list/template-groupings-list.component";
import { AddEditCompanyComponent } from "./view/dashboard/add-edit-company/add-edit-company.component";
import { AddEditContactComponent } from "./view/dashboard/add-edit-contact/add-edit-contact.component";
import { AddEditUserComponent } from "./view/dashboard/add-edit-user/add-edit-user.component";
import { DealListComponent } from "./view/dashboard/deal-list/deal-list.component";
import { EditDealComponent } from "./view/dashboard/edit-deal/edit-deal.component";
import { CollaborationAccessComponent } from "./view/dashboard/collaboration-access/collaboration-access.component";
import { CollaboratorListComponent } from "./view/shared/collaborator-list/collaborator-list.component";
import { CollaborationDiscussionsComponent } from "./view/dashboard/collaboration-discussions/collaboration-discussions.component";
import { CollaborationRemindersComponent } from "./view/dashboard/collaboration-reminders/collaboration-reminders.component";
import { CollaborationNotificationSettingsComponent } from "./view/dashboard/collaboration-notification-settings/collaboration-notification-settings.component";
import { CollaborationPresenceComponent } from "./view/shared/collaboration-presence/collaboration-presence.component";
import { EditSurveyComponent } from "./view/dashboard/edit-survey/edit-survey.component";
import { ReviewAllCommentsScansComponent } from "./view/dashboard/review-all-comments-scans/review-all-comments-scans.component";
import { ReviewCommentsScansComponent } from "./view/dashboard/review-comments-scans/review-comments-scans.component";
import { DashboardComponent } from "./view/dashboard/survey-dashboard/survey-dashboard.component";
import { ViewDealComponent } from "./view/dashboard/view-deal/view-deal.component";
import { ViewResponseComponent } from "./view/dashboard/view-response/view-response.component";
import { ViewResponsesComponent } from "./view/dashboard/view-responses/view-responses.component";
import { NotFoundComponent } from "./view/error/not-found/not-found.component";
import { OnlineFormComponent } from "./view/online-form/online-form.component";
import { EditColleaguesComponent } from "./view/shared/edit-colleagues/edit-colleagues.component";
import { FilterMultiselectComponent } from "./view/shared/filter-multiselect/filter-multiselect.component";
import { EnumResponseComponent } from "./view/shared/question-response/enum-response/enum-response.component";
import { RangeResponseComponent } from "./view/shared/question-response/range-response/range-response.component";
import { SharedModule } from "./view/shared/shared.module";
import { UserAdminComponent } from "./view/user-admin/user-admin.component";
import { UserDeviceComponent } from "./view/user-device/user-device.component";

// import { SurveyMetadataComponent } from "./view/dashboard-survey-products/survey-metadata/survey-metadata.component";
// import {SurveyBrandingComponent}  from "./view/dashboard-survey-products/survey-branding/survey-branding.component";
// import { AttachQuestionnairesComponent } from "./view/dashboard-survey-products/attach-questionnaires/attach-questionnaires.component";
// Custom date adapter to handle DD/MM/YYYY format for manual input
export class CustomDateAdapter extends NativeDateAdapter {
  override parse(value: any): Date | null {
    if (typeof value === "string" && value.trim()) {
      const parts = value.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          const date = new Date(year, month, day);
          // Check if the date is valid
          if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
            return date;
          }
        }
      }
    }
    return super.parse(value);
  }
}
@NgModule({
  declarations: [
    AddEditCompanyComponent,
    AddEditContactComponent,
    AddEditUserComponent,
    AdminDashboardComponent,
    AuditLogHistoryComponent,
    AuditLoggerUserImpersonationHistoryComponent,
    AuditLoggerComponent,
    AuditLoggerUserImpersonationComponent,
    BrandingComponent,
    CompanyBrandingComponent,
    EditDealComponent,
    GenerateBenchmarkComponent,
    DRODashboardComponent,
    SurveyProductsDashboardComponent,
    QuestionnaireLibraryComponent,
    CreateQuestionnaireComponent,
    EditColleaguesComponent,
    ClientDashboardComponent,
    CorporateReportingComponent,
    DashboardComponent,
    DealListComponent,
    EditSurveyComponent,
    ForgotPasswordComponent,
    LoginComponent,
    LoggedOutComponent,
    MFAVerificationComponent,
    MainTemplateComponent,
    NotFoundComponent,
    OnlineFormComponent,
    OptOutComponent,
    ResetPasswordComponent,
    StopAutoEmailsComponent,
    ViewDealComponent,
    ViewResponseComponent,
    ViewResponsesComponent,
    ReviewCommentsScansComponent,
    ReviewAllCommentsScansComponent,
    UserAdminComponent,
    UserDeviceComponent,
    SurveyProductWizardComponent,
    StepMetadataComponent,
    StepBrandingComponent,
    AttachQuestionnairesComponent,
    MergeFieldDictionaryComponent,
    StepPublishingWorkflowComponent,
    SurveyProductListComponent,
    TemplateGroupingsListComponent,
    TemplateGroupingDetailComponent,
    ListQuestionsComponent,
    FilterMultiselectComponent,
    AddEditQuestionComponent,
    EnumResponseComponent,
    RangeResponseComponent,
    QgroupListComponent,
    AddEditQgroupComponent,
    ListOptionsComponent,
    ListRangeOptionsComponent,
    AddRangeOptionsComponent,
    ListRangeVariantsComponent,
    AddEditRangeVariantsComponent,
    ListRangeWeightedComponent,
    AddEditRangeWeightedComponent,
    AddListOptionsComponent,
    ManageListVariantsComponent,
    AddEditListVariantsComponent,
    ResponseOptionsLibraryComponent,
    AddEditResponseOptionGroupComponent,
    ResponseOptionGroupDuplicateModalComponent,
    CollaborationAccessComponent,
    CollaboratorListComponent,
    CollaborationDiscussionsComponent,
    CollaborationRemindersComponent,
    CollaborationNotificationSettingsComponent,
  ],
  bootstrap: [MainTemplateComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    ColorCompactModule,
    CommonModule,
    CollaborationPresenceComponent,
    DragDropModule,
    FormsModule,
    FontAwesomeModule,
    MatIconModule,
    MatTableModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatSortModule,
    MatButtonToggleModule,
    NgxMatSelectSearchModule,
    NgbModule,
    NgIdleKeepaliveModule.forRoot(),
    QuillModule.forRoot({
      customOptions: [
        {
          import: "attributors/style/size",
          whitelist: [
            "8px",
            "9px",
            "10px",
            "11px",
            "12px",
            "14px",
            "16px",
            "18px",
            "20px",
            "24px",
            "28px",
            "32px",
            "36px",
            "48px",
            "72px",
          ],
        },
      ],
    }),
    ReactiveFormsModule,
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: environment.enableServiceWorker,
      registrationStrategy: "registerWhenStable:30000",
    }),
    SharedModule,
    DragulaModule.forRoot(),
    A11yModule,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: "en-AU" }, //displays date in dd/mm/yyyy format in mat date picker
    { provide: DateAdapter, useClass: CustomDateAdapter },
    ModalService,
    /** Sentry */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      const initializerFn = () => {
        inject(Sentry.TraceService);
        inject(ModalService);
        inject(ThemeService);
        inject(VersionService);
        return Promise.resolve();
      };
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
